/* ==========================================================================
   Custom featured collection — carousel + quick add
   Self-contained. No jQuery, no theme dependencies, no globals leaked.
   Safe to run multiple times (Shopify theme editor re-renders sections).
   ========================================================================== */
(function () {
  'use strict';

  var INIT_FLAG = 'cfcInit';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ----------------------------------------------------------------------
     Carousel
     ---------------------------------------------------------------------- */
  function initCarousel(section) {
    var viewport = section.querySelector('[data-cfc-viewport]');
    if (!viewport) return;

    var track = viewport.querySelector('.cfc__grid');
    var items = track ? Array.prototype.slice.call(track.children) : [];
    if (items.length === 0) return;

    var prev = section.querySelector('[data-cfc-prev]');
    var next = section.querySelector('[data-cfc-next]');
    var dotsWrap = section.querySelector('[data-cfc-dots]');
    var loop = section.dataset.loop === 'true';
    var autoplay = section.dataset.autoplay === 'true';
    var delay = (parseInt(section.dataset.autoplaySpeed, 10) || 5) * 1000;
    var timer = null;

    function step() {
      // width of one item plus the gap between items
      if (items.length < 2) return viewport.clientWidth;
      var a = items[0].getBoundingClientRect();
      var b = items[1].getBoundingClientRect();
      var d = Math.abs(b.left - a.left);
      return d > 1 ? d : a.width;
    }

    function maxScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function overflowing() {
      return maxScroll() > 2;
    }

    function go(dir) {
      var max = maxScroll();
      var pos = viewport.scrollLeft;
      var target = pos + dir * step();

      if (loop) {
        if (dir > 0 && pos >= max - 2) target = 0;
        if (dir < 0 && pos <= 2) target = max;
      }
      viewport.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
    }

    function currentIndex() {
      var s = step();
      return s > 0 ? Math.round(viewport.scrollLeft / s) : 0;
    }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      if (!overflowing()) return;
      var s = step();
      var pages = s > 0 ? Math.ceil((maxScroll() + viewport.clientWidth) / s) : 0;
      // one dot per item position that can become the first visible card
      var count = Math.min(items.length, Math.max(1, Math.round(maxScroll() / s) + 1));
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cfc__dot';
        dot.setAttribute('aria-label', 'Go to item ' + (i + 1));
        dot.dataset.index = String(i);
        dotsWrap.appendChild(dot);
      }
      dotsWrap.removeAttribute('aria-hidden');
    }

    function syncUi() {
      var show = overflowing();

      if (prev) {
        prev.hidden = !show;
        prev.disabled = !loop && viewport.scrollLeft <= 2;
      }
      if (next) {
        next.hidden = !show;
        next.disabled = !loop && viewport.scrollLeft >= maxScroll() - 2;
      }
      if (dotsWrap) {
        var idx = currentIndex();
        Array.prototype.forEach.call(dotsWrap.children, function (d, i) {
          d.classList.toggle('is-active', i === idx);
        });
      }
    }

    function stopAuto() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function startAuto() {
      if (!autoplay) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      stopAuto();
      timer = setInterval(function () {
        if (document.hidden || !overflowing()) return;
        go(1);
      }, delay);
    }

    if (prev) prev.addEventListener('click', function () { go(-1); startAuto(); });
    if (next) next.addEventListener('click', function () { go(1); startAuto(); });

    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (e) {
        var dot = e.target.closest('.cfc__dot');
        if (!dot) return;
        viewport.scrollTo({ left: parseInt(dot.dataset.index, 10) * step(), behavior: 'smooth' });
        startAuto();
      });
    }

    viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    });

    var raf = null;
    viewport.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; syncUi(); });
    }, { passive: true });

    section.addEventListener('mouseenter', stopAuto);
    section.addEventListener('mouseleave', startAuto);
    section.addEventListener('focusin', stopAuto);
    section.addEventListener('focusout', startAuto);

    var ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function () { buildDots(); syncUi(); });
      ro.observe(viewport);
    } else {
      window.addEventListener('resize', function () { buildDots(); syncUi(); });
    }

    // images loading can change scrollWidth
    Array.prototype.forEach.call(section.querySelectorAll('img'), function (img) {
      if (!img.complete) {
        img.addEventListener('load', function () { buildDots(); syncUi(); }, { once: true });
      }
    });

    buildDots();
    syncUi();
    startAuto();

    section[INIT_FLAG + 'Teardown'] = function () {
      stopAuto();
      if (ro) ro.disconnect();
    };
  }

  /* ----------------------------------------------------------------------
     Quick add
     ---------------------------------------------------------------------- */
  function initQuickAdd(section) {
    var action = section.dataset.cartAction || 'ajax';

    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cfc-add]');
      if (!btn || !section.contains(btn)) return;

      e.preventDefault();
      e.stopPropagation();

      var id = btn.dataset.variantId;
      if (!id) return;
      if (btn.disabled) return;

      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = '…';

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add-failed');
          return res.json();
        })
        .then(function () {
          if (action === 'cart') {
            window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root : '/') + 'cart';
            return;
          }
          btn.textContent = btn.dataset.addedText || 'Added';
          btn.classList.add('is-added');

          // let the theme know, so a cart drawer or bubble can refresh itself
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:build', { bubbles: true })
          );

          setTimeout(function () {
            btn.textContent = original;
            btn.classList.remove('is-added');
            btn.disabled = false;
          }, 2000);
        })
        .catch(function () {
          // fall back to the product page rather than leaving a dead button
          btn.textContent = original;
          btn.disabled = false;
          var link = btn.closest('.cfc__card');
          var href = link && link.querySelector('.cfc__media-link');
          if (href) window.location.href = href.getAttribute('href');
        });
    });
  }

  /* ----------------------------------------------------------------------
     Quick view
     ---------------------------------------------------------------------- */
  var QV_VARS = [
    '--cfc-qv-overlay', '--cfc-qv-bg', '--cfc-qv-text', '--cfc-qv-price',
    '--cfc-qv-sale', '--cfc-qv-compare', '--cfc-qv-atc-bg', '--cfc-qv-atc-text',
    '--cfc-qv-atc-border', '--cfc-qv-buy-bg', '--cfc-qv-buy-text',
    '--cfc-qv-swatch-border', '--cfc-qv-swatch-active', '--cfc-qv-radius'
  ];

  var qv = {
    root: null,
    dialog: null,
    content: null,
    trigger: null,
    controller: null,
    scrollY: 0,
    open: false
  };

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function buildModal() {
    if (qv.root) return qv.root;

    var root = document.createElement('div');
    root.className = 'cqv-modal';
    root.hidden = true;
    root.innerHTML =
      '<div class="cqv-modal__overlay" data-cqv-overlay></div>' +
      '<div class="cqv-modal__dialog" role="dialog" aria-modal="true" aria-label="Product quick view" tabindex="-1">' +
      '<div data-cqv-content></div>' +
      '</div>';
    document.body.appendChild(root);

    qv.root = root;
    qv.dialog = root.querySelector('.cqv-modal__dialog');
    qv.content = root.querySelector('[data-cqv-content]');

    root.querySelector('[data-cqv-overlay]').addEventListener('click', closeQuickView);
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-cqv-close]')) closeQuickView();
    });

    return root;
  }

  function lockScroll() {
    qv.scrollY = window.scrollY || window.pageYOffset;
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.overflow = 'hidden';
    if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
  }

  function unlockScroll() {
    document.documentElement.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  function focusables() {
    if (!qv.dialog) return [];
    return Array.prototype.filter.call(
      qv.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
      function (el) { return el.offsetParent !== null; }
    );
  }

  function onKeydown(e) {
    if (!qv.open) return;
    if (e.key === 'Escape') { e.preventDefault(); closeQuickView(); return; }
    if (e.key !== 'Tab') return;

    var list = focusables();
    if (list.length === 0) return;
    var first = list[0];
    var last = list[list.length - 1];

    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function closeQuickView() {
    if (!qv.open) return;
    qv.open = false;
    if (qv.controller) { qv.controller.abort(); qv.controller = null; }
    qv.root.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown, true);
    unlockScroll();

    var t = qv.trigger;
    qv.trigger = null;

    window.setTimeout(function () {
      if (qv.open) return;
      qv.root.hidden = true;
      qv.content.innerHTML = '';
      if (t && document.contains(t)) t.focus();
    }, 220);
  }

  function openQuickView(url, section, trigger) {
    buildModal();

    qv.trigger = trigger || null;
    qv.open = true;
    qv.root.hidden = false;
    qv.content.innerHTML = '<span class="cqv-modal__spinner" role="status" aria-label="Loading"></span>';

    // copy this section's popup colours onto the modal
    if (section) {
      var cs = getComputedStyle(section);
      QV_VARS.forEach(function (v) {
        var val = cs.getPropertyValue(v);
        if (val) qv.root.style.setProperty(v, val.trim());
      });
    }

    lockScroll();
    document.addEventListener('keydown', onKeydown, true);
    requestAnimationFrame(function () { qv.root.classList.add('is-open'); });
    qv.dialog.focus();

    if (qv.controller) qv.controller.abort();
    qv.controller = ('AbortController' in window) ? new AbortController() : null;

    var sep = url.indexOf('?') > -1 ? '&' : '?';
    fetch(url + sep + 'section_id=custom-quick-view', {
      signal: qv.controller ? qv.controller.signal : undefined,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('fetch-failed');
        return res.text();
      })
      .then(function (html) {
        if (!qv.open) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-cqv-root]');
        if (!node) throw new Error('no-root');

        qv.content.innerHTML = '';
        qv.content.appendChild(document.importNode(node, true));
        initQuickViewContent(qv.content, url);

        var f = focusables();
        if (f.length) f[0].focus();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (!qv.open) return;
        qv.content.innerHTML =
          '<p class="cqv-modal__error">Could not load this product. ' +
          '<a href="' + url + '">Open the product page instead</a>.</p>';
      });
  }

  /* ---- everything inside the loaded popup ---- */
  function initQuickViewContent(scope, productUrl) {
    var root = scope.querySelector('[data-cqv-root]');
    if (!root) return;

    var form = root.querySelector('[data-cqv-form]');
    var idInput = root.querySelector('[data-cqv-variant-id]');
    var priceEl = root.querySelector('[data-cqv-price]');
    var compareEl = root.querySelector('[data-cqv-compare]');
    var saveEl = root.querySelector('[data-cqv-save]');
    var mainImg = root.querySelector('[data-cqv-main]');
    var addBtn = root.querySelector('[data-cqv-add]');
    var addText = root.querySelector('[data-cqv-add-text]');
    var buyBtn = root.querySelector('[data-cqv-buy]');
    var qtyInput = root.querySelector('[data-cqv-qty-input]');
    var gallery = root.querySelector('.cqv__gallery');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-cqv-thumb]'));

    if (gallery && thumbs.length === 0) gallery.classList.add('cqv__gallery--single');

    var variants = [];
    var dataEl = root.querySelector('[data-cqv-variants]');
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent) || []; } catch (e) { variants = []; }
    }

    /* ---- thumbnails ---- */
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        if (!mainImg) return;
        mainImg.src = t.dataset.full;
        mainImg.alt = t.dataset.alt || '';
        thumbs.forEach(function (x) { x.classList.toggle('is-active', x === t); });
      });
    });

    function markActiveThumb(src) {
      if (!src) return;
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', t.dataset.full === src);
      });
    }

    /* ---- options ---- */
    var fieldsets = Array.prototype.slice.call(root.querySelectorAll('[data-cqv-option]'));

    function selection() {
      return fieldsets.map(function (fs) {
        var checked = fs.querySelector('[data-cqv-opt]:checked');
        return checked ? checked.value : null;
      });
    }

    function matchVariant(sel) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var j = 0; j < sel.length; j++) {
          if (sel[j] !== null && v.options[j] !== sel[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    function markUnavailable() {
      var sel = selection();
      fieldsets.forEach(function (fs, idx) {
        Array.prototype.forEach.call(fs.querySelectorAll('[data-cqv-opt]'), function (input) {
          var test = sel.slice();
          test[idx] = input.value;
          var hit = variants.some(function (v) {
            for (var j = 0; j < test.length; j++) {
              if (test[j] !== null && v.options[j] !== test[j]) return false;
            }
            return v.available;
          });
          var label = fs.querySelector('label[for="' + input.id + '"]');
          if (label) label.classList.toggle('is-unavailable', !hit);
        });
      });
    }

    function update() {
      var sel = selection();
      var variant = variants.length ? matchVariant(sel) : null;

      // single-variant products have no fieldsets
      if (!variant && variants.length === 1) variant = variants[0];

      var labelSpan = null;
      fieldsets.forEach(function (fs) {
        labelSpan = fs.querySelector('[data-cqv-option-value]');
        if (!labelSpan) return;
        var checked = fs.querySelector('[data-cqv-opt]:checked');
        if (checked && labelSpan.textContent.trim() !== '') {
          labelSpan.textContent = '— ' + checked.value;
        }
      });

      markUnavailable();

      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (buyBtn) buyBtn.disabled = true;
        if (addText) addText.textContent = 'UNAVAILABLE';
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = variant.price;

      if (compareEl) {
        if (variant.compare) { compareEl.textContent = variant.compare; compareEl.hidden = false; }
        else { compareEl.hidden = true; }
      }
      if (saveEl) {
        if (variant.save !== null && variant.save !== undefined) {
          saveEl.textContent = 'Save ' + variant.save + '%';
          saveEl.hidden = false;
        } else { saveEl.hidden = true; }
      }
      if (priceEl) priceEl.classList.toggle('cqv__price--sale', !!variant.compare);

      if (variant.image && mainImg) {
        mainImg.src = variant.image;
        markActiveThumb(variant.image);
      }

      if (addBtn) addBtn.disabled = !variant.available;
      if (buyBtn) buyBtn.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
    }

    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-cqv-opt]')) update();
    });

    /* ---- quantity ---- */
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cqv-qty]');
      if (!btn || !qtyInput) return;
      var next = (parseInt(qtyInput.value, 10) || 1) + parseInt(btn.dataset.cqvQty, 10);
      qtyInput.value = next < 1 ? 1 : next;
    });
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var n = parseInt(qtyInput.value, 10);
        if (!n || n < 1) qtyInput.value = 1;
      });
    }

    /* ---- add to cart ---- */
    function addToCart(then) {
      if (!idInput) return Promise.reject(new Error('no-variant'));
      var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

      return fetch(routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(idInput.value), quantity: qty }] })
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || 'add-failed'); });
        return res.json();
      }).then(then);
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!addBtn || addBtn.disabled) return;

        var original = addText ? addText.textContent : '';
        addBtn.disabled = true;
        if (addText) addText.textContent = 'ADDING…';

        addToCart(function () {
          if (addText) addText.textContent = 'ADDED ✓';
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          window.setTimeout(function () {
            closeQuickView();
            if (addText) addText.textContent = original;
            addBtn.disabled = false;
          }, 900);
        }).catch(function (err) {
          if (addText) addText.textContent = (err && err.message) ? 'UNAVAILABLE' : original;
          window.setTimeout(function () {
            if (addText) addText.textContent = original;
            addBtn.disabled = false;
          }, 1800);
        });
      });
    }

    /* ---- buy it now ---- */
    if (buyBtn) {
      buyBtn.addEventListener('click', function () {
        if (buyBtn.disabled) return;
        buyBtn.disabled = true;
        buyBtn.textContent = 'ADDING…';
        addToCart(function () {
          window.location.href = routeRoot() + 'checkout';
        }).catch(function () {
          buyBtn.disabled = false;
          buyBtn.textContent = 'BUY IT NOW';
          window.location.href = productUrl;
        });
      });
    }

    update();
  }

  function initQuickView(section) {
    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cfc-quickview]');
      if (!btn || !section.contains(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      openQuickView(btn.dataset.productUrl, section, btn);
    });
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  /* The theme's own quick view binds once on page load by scanning for
     .grid-product. Our cards may not exist yet at that moment (and never do
     in the theme editor), so ask the theme to bind again. Harmless if it is
     already bound: its handlers remove themselves on first fire. */
  function rebindThemeQuickShop(section) {
    if (!section.querySelector('.grid-product[data-product-id]')) return;
    var t = window.theme;
    if (!t || typeof t.initQuickShop !== 'function') return;
    try { t.initQuickShop(); } catch (e) { /* theme internals changed; ignore */ }
  }

  function initSection(section) {
    if (!section || section.dataset[INIT_FLAG] === 'true') return;
    section.dataset[INIT_FLAG] = 'true';
    initCarousel(section);
    initQuickAdd(section);
    initQuickView(section);
    rebindThemeQuickShop(section);
  }

  function initAll(root) {
    var scope = root || document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-cfc-section]'),
      initSection
    );
  }

  ready(function () { initAll(); });

  // Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var s = e.target.querySelector('[data-cfc-section]');
    if (s && typeof s[INIT_FLAG + 'Teardown'] === 'function') s[INIT_FLAG + 'Teardown']();
  });
  document.addEventListener('shopify:section:select', function (e) {
    initAll(e.target);
  });
})();